/* import __COLOCATED_TEMPLATE__ from './lsg-tiny-mce-editor.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { loadScript } from "../../utils/script-loader";
import config from 'sh-www/config/environment';

/**
 * How many times to try to reload the tiny-mce script before stopping.
 * @type {number}
 */
const TINY_MCE_SCRIPT_LOAD_RETRY_COUNT = 3;

/**
 * A wrapper for our LSG tiny mce editor.
 */
const LsgTinyMCEEditor = Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['LSG-c-tiny-mce-editor'],
  classNameBindings: ['isDisabled:LSG-c-tiny-mce-editor--disabled'],

  /**
   * Register services
   */
  errorTracker: service('error-tracker'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /**
   * @param {String}    The value to be populated inside the editor
   */
  editorValue: '',

   /**
   * @param {Function}
   */
  onchange: () => {},

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /**
   * The inputId or we'll generate a unique one.
   *
   * @param {String=}
   */
  inputId: computed(function() {
    return 'lsg-c-tiny-mce-editor' + this.get('elementId');
  }),

  /**
   * @param {Boolean=}    Set to true if you want to disable the component.
   */
  isDisabled: false,

  /**
   * @param {string} name Add a name for the tinyMce editor
   */
  name: '',

  /**
   * @param {String=}   Add a placeholder for the tinyMce editor
   */
  placeholder: '',

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /**
   * @inheritDoc
   */
  didInsertElement() {
    let tinyUri = `${config.SH_CDN_JS_BASE_URL}/thirdparties/tinymce/tinymce.min.js`;

    /**
     * Dot he work of trying to load tiny-mce.js and recursively call again if it fails until the retry count.
     *
     * @param {Number} retryCount
     */
    let loadTinyMCE = (retryCount) => {
      if (retryCount <= 0) {
        this.get('errorTracker').error('could not load tinyMCE script after all retries', this);
        return;
      }

      if (!LsgTinyMCEEditor.TINY_SCRIPT_LOADING_PROMISE) {
        // we have NOT made a call to load the script yet
        LsgTinyMCEEditor.TINY_SCRIPT_LOADING_PROMISE = loadScript(tinyUri).then(() => {
          let tinyMCE = window.tinyMCE,
            baseJSUri = config.SH_CDN_JS_BASE_URL;

          tinyMCE.baseURL = baseJSUri + '/thirdparties/tinymce';
          tinyMCE.suffix = '.min';

          scheduleOnce('afterRender', this, function() {
            this.set('_isTinyMCEInitialized', true);
          });
        }).catch(e => {
          this.get('errorTracker').error('could not load tinyMCE script', this, e);

          LsgTinyMCEEditor.TINY_SCRIPT_LOADING_PROMISE = null;

          // try one second later.
          setTimeout(() => loadTinyMCE(--retryCount), 1000);

          throw e;
        });

        return;
      }

      // at this point, script would be loading from another instance, so just add yourself to the promise chain.
      LsgTinyMCEEditor.TINY_SCRIPT_LOADING_PROMISE.then(() => {
        scheduleOnce('afterRender', this, function() {
          this.set('_isTinyMCEInitialized', true);
        });
      }).catch(e => {
        this.get('errorTracker').error('could not load tinyMCE script', this, e);

        // try one second later.
        setTimeout(() => loadTinyMCE(--retryCount), 1000);

        throw e;
      });
    };

    if (!window.tinyMCE) {
      //script has not been loaded
      loadTinyMCE(TINY_MCE_SCRIPT_LOAD_RETRY_COUNT);
    } else {
      scheduleOnce('afterRender', this, function() {
        this.set('_isTinyMCEInitialized', true);
      });
    }
  },

  /**
   * @inheritDoc
   */
  didReceiveAttrs() {
    let oldIsDisabled = this.get('_oldIsDisabled'),
      isDisabled = this.get('isDisabled');

    if (!oldIsDisabled && isDisabled) {
      this._toggleEditorDisabled(true);
    }

    if (!isDisabled && oldIsDisabled) {
      this._toggleEditorDisabled(false);
    }

    this.set('_oldIsDisabled', isDisabled);
  },

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * - used for lifecycle method to keep track of changing states on re-render.
   *
   * @param {Boolean=}    Set to true if you want to disable the component.
   */
  _oldIsDisabled: false,

  /**
   * Try to toggle the component disabled/enabled.
   *
   * @param {Boolean} isDisabled
   * @private
   */
  _toggleEditorDisabled(isDisabled) {
    let tinyMCEIframeEl = document.getElementById(this.elementId)?.querySelector('textarea');
    if (!tinyMCEIframeEl) {
      return;
    }

    let editorId = tinyMCEIframeEl.id,
      editor = window.tinyMCE.get(editorId);
    if (!editor) {
      return;
    }

    editor.getBody().setAttribute('contenteditable', !isDisabled);
  },

  /**
   * @type {Boolean}      True if we know the script has been pulled in.
   */
  _isTinyMCEInitialized: false,

  /**
   * These are the options for the tinyMCE editor.
   *
   * @type {Object}
   */
  _tinyMceOptions: computed(function() {
    const editorElId = `#${this.get('inputId')}`;
    const baseCSSUri = config.SH_CDN_CSS_BASE_URL;
    const {
      name,
      placeholder,
    } = this

    return {
      selector: editorElId,
      skin_url: baseCSSUri + '/thirdparties/tinymce/skins/lightgray',
      browser_spellcheck: true,
      content_css: ['https://fonts.googleapis.com/css?family=Source+Sans+Pro'],
      content_style: 'body.mce-content-body {' +
        'margin-left: 0;' +
        'font-family: \'Source Sans Pro\', sans-serif;' +
        'font-size: 16px;' +
        'font-weight: 400;' +
        'line-height: 1.5;' +
        'letter-spacing: -0.18px;' +
        'background: #FCFCFC;' +
      '}',
      width: '100%',
      theme: 'modern',
      name,
      placeholder,
      plugins: ["anchor link autolink autoresize placeholder"],
      statusbar: false,
      autoresize_min_height: 175,
      autoresize_max_height: 275,
      menubar: false,
      toolbar: "bold italic underline | bullist numlist",
      // **** NOTE - If you update these HTML tags, you need to update what is allowed in the tiny-mce editors ****
      valid_elements: 'a[href|target=_blank],strong/b,br,p,ul,ol,li,i,u,em,span[style]',
      valid_styles: {"*": "text-decoration"},
      toggletoolbars_status: "off",
      theme_advanced_toolbar_location : "bottom",
      convert_urls: false,
      setup: (ed) => {
        ed.on('Init', () => {
          // nothing for now.
        });
      }
    };
  }),
});

LsgTinyMCEEditor.reopenClass({
  /**
   * This is our static promise for loading in the tinymce script.
   *
   * @type {Ember.RSVP.Promise|null}
   */
  TINY_SCRIPT_LOADING_PROMISE: null
});

export default LsgTinyMCEEditor;
